import { useState, type ReactNode } from 'react';
import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Caption } from '@/components/ui/Text';

type OfflineAccessCardShellProps = {
  icon: keyof typeof Ionicons.glyphMap;
  /** When true, use primary-tinted icon well (downloaded / ready state). */
  active?: boolean;
  title: string;
  subtitle: string;
  /** Trailing actions (buttons, badges). */
  trailing?: ReactNode;
  /** Extra rows below the main row (e.g. captions, secondary buttons). */
  footer?: ReactNode;
  style?: import('react-native').ViewStyle;
  /**
   * When true, only title + subtitle stay visible until the user expands.
   * Use for long footers (e.g. Documents offline card). Ignored if `footer` is empty.
   */
  collapsible?: boolean;
  /** Initial expand state when `collapsible` is true. */
  defaultExpanded?: boolean;
};

/**
 * Shared layout for offline download / status cards (Plan, Documents, Safety, settings).
 */
export function OfflineAccessCardShell({
  icon,
  active = false,
  title,
  subtitle,
  trailing,
  footer,
  style,
  collapsible = false,
  defaultExpanded = false,
}: OfflineAccessCardShellProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const iconWell = (
    <View
      style={{
        width: 44,
        height: 44,
        borderRadius: theme.radii.lg,
        backgroundColor: active ? theme.colors.primary + '18' : theme.colors.surface,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: active ? 2 : 1,
        borderColor: active ? theme.colors.primary + '55' : theme.colors.borderMuted,
      }}
    >
      <Ionicons name={icon} size={22} color={active ? theme.colors.primary : theme.colors.textSecondary} />
    </View>
  );

  const titleBlock = (
    <View style={{ flex: 1, gap: theme.spacing(0.35), minWidth: 0 }}>
      <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
        {title}
      </ThemedText>
      <Caption color={theme.colors.textSecondary}>{subtitle}</Caption>
    </View>
  );

  const showCollapsible = collapsible && Boolean(footer);

  if (showCollapsible) {
    return (
      <Card variant="outlined" style={[{ marginBottom: theme.spacing(2) }, style]}>
        <View style={{ gap: theme.spacing(1.25) }}>
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: theme.spacing(1.5),
              opacity: pressed ? 0.7 : 1,
            })}
            accessibilityRole="button"
            accessibilityLabel={`${title}. ${expanded ? 'Expanded' : 'Collapsed'}`}
            accessibilityHint="Double tap to show or hide offline details and actions"
            accessibilityState={{ expanded }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(2),
                flex: 1,
                minWidth: 0,
              }}
            >
              {iconWell}
              {titleBlock}
            </View>
            <Ionicons
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={22}
              color={theme.colors.textSecondary}
              style={{ marginTop: theme.spacing(0.5) }}
            />
          </Pressable>
          {expanded ? footer : null}
        </View>
      </Card>
    );
  }

  return (
    <Card variant="outlined" style={[{ marginBottom: theme.spacing(2) }, style]}>
      <View style={{ gap: theme.spacing(1.25) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(2),
              flex: 1,
              minWidth: 200,
            }}
          >
            {iconWell}
            {titleBlock}
          </View>
          {trailing ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' }}>
              {trailing}
            </View>
          ) : null}
        </View>
        {footer}
      </View>
    </Card>
  );
}
